import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import { adminService, type SessionWritePayload } from '../../services/adminService'
import type { Session, Speaker } from '../../data/types'
import { EmptyState, LoadingState } from '../../components/ui/States'
import SessionTypeChip from '../../components/ui/SessionTypeChip'
import { formatShortDay, formatTimeRange } from '../../utils/format'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'
import TargetingSelect from './TargetingSelect'
import AssetUploadButton from '../AssetUploadButton'

const TYPES = ['keynote', 'session', 'panel', 'workshop', 'meal', 'networking', 'break']

function toLocalInput(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16)
}
const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : '')

export default function SessionsManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [sessions, setSessions] = useState<Session[]>([])
  const [speakers, setSpeakers] = useState<Speaker[]>([])
  const [agendaStatus, setAgendaStatus] = useState<string>('published')
  const [savingStatus, setSavingStatus] = useState(false)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Session | null>(null)
  const [open, setOpen] = useState(false)

  const load = () =>
    Promise.all([
      adminService.listSessions(eventId),
      adminService.listSpeakers(eventId),
      adminService.getEvent(eventId),
    ])
      .then(([s, sp, ev]) => {
        setSessions(s)
        setSpeakers(sp)
        setAgendaStatus(ev.agendaStatus ?? 'published')
      })
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  const setStatus = async (published: boolean) => {
    const next = published ? 'published' : 'draft'
    setSavingStatus(true)
    try {
      await adminService.updateEvent(eventId, { agendaStatus: next })
      setAgendaStatus(next)
      notify(published ? 'Agenda published' : 'Agenda set to draft (hidden from attendees)')
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSavingStatus(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const remove = async (s: Session) => {
    if (!(await confirmDelete(`Delete session "${s.title}"?`))) return
    try {
      await adminService.deleteSession(eventId, s.id)
      notify('Session deleted')
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />

  const isPublished = agendaStatus !== 'draft'

  return (
    <Box>
      <Card
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={700}>
              Agenda status
            </Typography>
            <Chip
              size="small"
              label={isPublished ? 'Published' : 'Draft'}
              color={isPublished ? 'success' : 'default'}
              sx={{ fontWeight: 700, textTransform: 'capitalize' }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {isPublished
              ? 'The agenda is visible to attendees.'
              : 'Draft: the agenda is hidden from attendees (nav + sessions) until you publish it.'}
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={isPublished}
              disabled={savingStatus}
              onChange={(e) => setStatus(e.target.checked)}
            />
          }
          label={isPublished ? 'Published' : 'Draft'}
        />
      </Card>

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={() => {
            setEditing(null)
            setOpen(true)
          }}
        >
          Add session
        </Button>
      </Stack>

      {sessions.length === 0 ? (
        <EmptyState title="No sessions yet" description="Build your agenda by adding sessions." />
      ) : (
        <Stack spacing={1.5}>
          {sessions.map((s) => (
            <Card
              key={s.id}
              variant="outlined"
              sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <SessionTypeChip type={s.type} />
                  {s.track && (
                    <Chip label={s.track} size="small" variant="outlined" sx={{ height: 20 }} />
                  )}
                </Stack>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {s.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {formatShortDay(s.startsAt)} · {formatTimeRange(s.startsAt, s.endsAt)}
                  {s.location ? ` · ${s.location}` : ''}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setEditing(s)
                  setOpen(true)
                }}
              >
                <EditRoundedIcon />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => remove(s)}>
                <DeleteRoundedIcon />
              </IconButton>
            </Card>
          ))}
        </Stack>
      )}

      {open && (
        <SessionDialog
          eventId={eventId}
          session={editing}
          speakers={speakers}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false)
            load()
          }}
        />
      )}
    </Box>
  )
}

function SessionDialog({
  eventId,
  session,
  speakers,
  onClose,
  onSaved,
}: {
  eventId: string
  session: Session | null
  speakers: Speaker[]
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    title: session?.title ?? '',
    description: session?.description ?? '',
    startsAt: toLocalInput(session?.startsAt),
    endsAt: toLocalInput(session?.endsAt),
    location: session?.location ?? '',
    track: session?.track ?? '',
    type: session?.type ?? 'session',
    recordingUrl: session?.recordingUrl ?? '',
    speakerIds: session?.speakerIds ?? [],
    targetingRuleId: session?.targetingRuleId ?? null,
  })
  const set = (k: keyof typeof form, v: unknown) => setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.title || !form.startsAt || !form.endsAt) {
      notify('Title, start and end are required', 'error')
      return
    }
    const payload: SessionWritePayload = {
      title: form.title,
      description: form.description || undefined,
      startsAt: fromLocalInput(form.startsAt),
      endsAt: fromLocalInput(form.endsAt),
      location: form.location || undefined,
      track: form.track || undefined,
      type: form.type,
      recordingUrl: form.recordingUrl || null,
      speakerIds: form.speakerIds,
      targetingRuleId: form.targetingRuleId,
    }
    setSaving(true)
    try {
      if (session) await adminService.updateSession(eventId, session.id, payload)
      else await adminService.createSession(eventId, payload)
      notify(session ? 'Session updated' : 'Session added')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{session ? 'Edit session' : 'Add session'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            required
            fullWidth
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Starts"
                type="datetime-local"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={form.startsAt}
                onChange={(e) => set('startsAt', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ends"
                type="datetime-local"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={form.endsAt}
                onChange={(e) => set('endsAt', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Location"
                fullWidth
                value={form.location}
                onChange={(e) => set('location', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Track"
                fullWidth
                value={form.track}
                onChange={(e) => set('track', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Type"
                select
                fullWidth
                value={form.type}
                onChange={(e) => set('type', e.target.value)}
              >
                {TYPES.map((t) => (
                  <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Box>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Recording URL (YouTube/Vimeo, uploaded file, or link)"
                fullWidth
                value={form.recordingUrl}
                onChange={(e) => set('recordingUrl', e.target.value)}
                helperText="Paste a YouTube/Vimeo link, or upload a video file to stream on demand"
              />
              <AssetUploadButton
                eventId={eventId}
                accept="video/mp4,video/webm,video/ogg,video/quicktime"
                label="Upload video"
                uploader={adminService.uploadRecording}
                onUploaded={(url) => set('recordingUrl', url)}
              />
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Speakers
            </Typography>
            <Select
              multiple
              fullWidth
              size="small"
              value={form.speakerIds}
              onChange={(e) => set('speakerIds', e.target.value)}
              input={<OutlinedInput />}
              renderValue={(selected) => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {(selected as string[]).map((id) => {
                    const sp = speakers.find((x) => x.id === id)
                    return <Chip key={id} label={sp?.name ?? id} size="small" />
                  })}
                </Stack>
              )}
              sx={{ mt: 0.5 }}
            >
              {speakers.map((sp) => (
                <MenuItem key={sp.id} value={sp.id}>
                  {sp.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <TargetingSelect
            eventId={eventId}
            value={form.targetingRuleId}
            onChange={(v) => set('targetingRuleId', v)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
